/**
 * Main Application Entry Point
 * Initializes the app and loads Lit components
 */

import { loadConfigAsync, applyTheme, getActiveMachine, setActiveMachine, loadConfig, saveConfig } from './utils/config.js';
import { debug } from './utils/debug.js';
import { showToast, showConfirmDialog } from './utils/ui.js';
import { checkAuthStatus, login, logout } from './api/auth.js';
import './components/file-browser-logic.js';
import './components/overlay-viewer.js';
import { initTabManager, openTab, closeAllToolTabs, hasDirtyToolState } from './components/tab-manager.js';
import { initSidebar, refreshSidebarState } from './components/sidebar.js';

// Application state (in-memory only, no persistence)
window.appState = {
    currentPath: '/',
    files: [],
    selectedFiles: new Set(),
    // Clipboard state for copy/cut/paste operations
    clipboard: {
        operation: null, // 'copy' | 'cut' | null
        paths: [],       // Array of full paths
    },
    openTabs: [
        { id: 'files', type: 'files', label: 'Files', icon: 'folder_open', active: true }
    ],
    activeTabId: 'files',
    viewMode: 'list', // 'list' | 'grid' | 'thumb'
    sortBy: 'name',
    sortOrder: 'asc',
    activeMachine: 'Self', // Machine name string (e.g. 'Self', 'gcp-dev1')
};

let loginOverlayInitialized = false;
let pendingLoginMachine = null;
let pendingLoginResolver = null;
let loginOverlayRequired = true; // Whether login overlay can be dismissed with Escape

/**
 * Initialize application
 */
async function init() {
    debug.log('🚀 Admin Panel starting...');

    initLoginOverlay();

    // 1. Auth Self first — Self uses hardcoded relative paths, no config needed
    const selfMachine = { name: 'Self', isSelf: true };
    await ensureAuthenticatedMachine(selfMachine);

    // 2. Load config via authenticated backend FS API
    const config = await loadConfigAsync();
    window.appState.config = config;
    debug.log('📦 Config loaded:', config);

    applyTheme();
    
    // Load file browser component
    await loadComponents();
    
    // Initialize tab manager
    initTabManager();

    // Initialize Sidebar
    await initSidebar();
    
    // Initialize event listeners
    setupEventListeners();
    
    debug.log('✨ Admin Panel ready');
}

/**
 * Load Lit components dynamically
 */
async function loadComponents() {
    debug.log('📦 Loading components...');
    
    // Mount File Browser - replaces static HTML with dynamic Lit component
    const container = document.querySelector('.file-container');
    const existingListBox = document.querySelector('.file-list-box');
    const existingGridBox = document.querySelector('.file-grid-box');
    
    if (container) {
        // Remove static placeholder content
        if (existingListBox) existingListBox.remove();
        if (existingGridBox) existingGridBox.remove();
        
        const browser = document.createElement('file-browser-logic');
        browser.id = 'main-file-browser';
        container.appendChild(browser);
        debug.log('✅ FileBrowser mounted');
    }
}

/**
 * Update menu bar button states based on selection and clipboard
 */
function updateMenuBarState() {
    const btn = (action) => document.querySelector(`[data-action="${action}"]`);
    
    const hasSelection = window.appState.selectedFiles && window.appState.selectedFiles.size > 0;
    const selectionCount = window.appState.selectedFiles ? window.appState.selectedFiles.size : 0;
    const hasClipboard = window.appState.clipboard && window.appState.clipboard.paths.length > 0;
    
    // Buttons that require selection
    const selectionBtns = ['download', 'delete', 'copy', 'copy-path', 'cut', 'move-to'];
    selectionBtns.forEach(action => {
        const b = btn(action);
        if (b) b.disabled = !hasSelection;
    });
    
    // Paste requires clipboard
    const pasteBtn = btn('paste');
    if (pasteBtn) pasteBtn.disabled = !hasClipboard;
    
    // Rename requires exactly one selected item
    const renameBtn = btn('rename');
    if (renameBtn) renameBtn.disabled = selectionCount !== 1;
    
    // Edit: single file selection only (not folders)
    const editBtn = btn('edit');
    if (editBtn) {
        let editDisabled = true;
        if (selectionCount === 1) {
            const fileName = Array.from(window.appState.selectedFiles)[0];
            const innerBrowser = document.querySelector('file-browser');
            const file = innerBrowser?.files?.find(f => f.name === fileName || innerBrowser?._fullPath(f) === fileName);
            if (file && !file.is_dir) editDisabled = false;
        }
        editBtn.disabled = editDisabled;
    }
    
    // Up Level always enabled
    const upBtn = btn('up-level');
    if (upBtn) upBtn.disabled = false;
}

/**
 * Setup global event listeners
 */
function setupEventListeners() {
    const browser = document.getElementById('main-file-browser');
    const btn = (action) => document.querySelector(`[data-action="${action}"]`);
    
    // --- Toolbar Wiring (data-driven) ---
    if (browser) {
        const toolbarActions = {
            'upload':     () => browser.triggerUpload(),
            'new-file':   () => browser.createFile(),
            'new-folder': () => browser.createFolder(),
            'delete':     () => browser.triggerDelete(),
            'download':   () => browser.triggerDownload(),
            'copy':       () => browser.triggerCopy(),
            'copy-path':  () => browser.triggerCopyPath(),
            'cut':        () => browser.triggerCut(),
            'paste':      () => browser.triggerPaste(),
            'rename':     () => browser.triggerRename(),
            'move-to':    () => browser.triggerMoveTo(),
            'up-level':   () => browser.navigateUp(),
        };
        
        Object.entries(toolbarActions).forEach(([action, handler]) => {
            btn(action)?.addEventListener('click', handler);
        });
        
        // Edit (needs extra logic for folder check)
        btn('edit')?.addEventListener('click', () => {
            if (window.appState.selectedFiles && window.appState.selectedFiles.size === 1) {
                const fileName = Array.from(window.appState.selectedFiles)[0];
                const innerBrowser = browser.querySelector('file-browser');
                const file = innerBrowser?.files?.find(f => f.name === fileName || innerBrowser?._fullPath(f) === fileName);
                if (file && file.is_dir) {
                    showToast('Cannot edit a folder', 'warning');
                } else {
                    const path = fileName;
                    const label = fileName.split('/').pop() || fileName;
                    openTab('editor', { label: label, path: path });
                }
            }
        });
        
        // View mode toggle — cycles list → grid → thumb
        const VIEW_CYCLE = ['list', 'grid', 'thumb'];
        const VIEW_ICONS = { list: 'view_list', grid: 'widget_small', thumb: 'grid_view' };
        const viewToggleBtn = btn('view-toggle');
        viewToggleBtn?.addEventListener('click', () => {
            const current = window.appState.viewMode || 'list';
            const next = VIEW_CYCLE[(VIEW_CYCLE.indexOf(current) + 1) % VIEW_CYCLE.length];
            browser.setViewMode(next);
            viewToggleBtn.querySelector('.icon').textContent = VIEW_ICONS[next];
        });
    }
    
    // --- Breadcrumbs ---
    if (browser) {
        browser.addEventListener('path-changed', (e) => {
            updateBreadcrumbs(e.detail.path);
            updateMenuBarState();
        });
        updateBreadcrumbs(browser.currentPath);
        
        browser.addEventListener('app-open-file', (e) => {
            const { path, name } = e.detail;
            openTab('editor', { label: name, path: path });
        });
        
        browser.addEventListener('selection-changed', () => updateMenuBarState());
        window.addEventListener('clipboard-changed', () => updateMenuBarState());
    }
    
    updateMenuBarState();

    // --- Search Input Wiring ---
    const searchInput = document.querySelector('.search-input');
    const searchBox = document.querySelector('.searchbox');
    const searchClearBtn = document.querySelector('.search-clear');
    if (searchInput && browser) {
        const innerBrowser = () => browser.querySelector('file-browser');
        let _searchDebounce = null;

        // 7.1 — Debounced client-side filter on input
        searchInput.addEventListener('input', () => {
            const query = searchInput.value;
            // 7.5 — Toggle search-active class
            if (searchBox) searchBox.classList.toggle('search-active', query.length > 0);
            clearTimeout(_searchDebounce);
            _searchDebounce = setTimeout(() => {
                const fb = innerBrowser();
                if (fb) fb.applyClientFilter(query);
            }, 150);
        });

        // 7.2 + 7.3 — Enter triggers API search, Escape clears
        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const query = searchInput.value.trim();
                if (query) {
                    const fb = innerBrowser();
                    if (fb) fb.executeSearch(query);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                if (searchInput.value) {
                    searchInput.value = '';
                    if (searchBox) searchBox.classList.remove('search-active');
                    const fb = innerBrowser();
                    if (fb) fb.clearSearch();
                } else {
                    searchInput.blur();
                }
            }
        });

        // 7.4 — Clear button click
        if (searchClearBtn) {
            searchClearBtn.addEventListener('click', () => {
                searchInput.value = '';
                if (searchBox) searchBox.classList.remove('search-active');
                const fb = innerBrowser();
                if (fb) fb.clearSearch();
                searchInput.focus();
            });
        }
    }

    // Theme toggle
    const themeToggle = document.querySelector('.theme-toggle');
    const themeIcon = document.getElementById('theme-toggle-icon');
    if (themeToggle) {
        themeToggle.addEventListener('click', () => {
            const isDark = !document.documentElement.classList.contains('dark');
            localStorage.setItem('theme', isDark ? 'dark' : 'light');
            
            // Save to persistent config and apply dynamically (which updates meta theme-color too)
            const config = loadConfig();
            config.theme = isDark ? 'dark' : 'light';
            saveConfig(config);
            applyTheme();

            if (themeIcon) themeIcon.textContent = isDark ? 'light_mode' : 'dark_mode';
        });
        // Set initial icon state
        if (themeIcon) {
            const isDark = document.documentElement.classList.contains('dark');
            themeIcon.textContent = isDark ? 'light_mode' : 'dark_mode';
        }
    }
    
    // Sidebar toggle button
    const sidebarToggleBtn = btn('sidebar-toggle');
    if (sidebarToggleBtn) {
        sidebarToggleBtn.addEventListener('click', () => {
            document.documentElement.classList.toggle('sidebar-collapsed');
        });
    }

    // Mobile: start with sidebar collapsed
    if (window.innerWidth <= 800) {
        document.documentElement.classList.add('sidebar-collapsed');
    }

    // Mobile: backdrop click closes sidebar
    const sidebarBackdrop = document.getElementById('sidebar-backdrop');
    if (sidebarBackdrop) {
        sidebarBackdrop.addEventListener('click', () => {
            document.documentElement.classList.add('sidebar-collapsed');
        });
    }

    // Mobile: swipe gestures to open/close sidebar
    {
        let touchStartX = 0;
        let touchStartY = 0;
        const SWIPE_THRESHOLD = 50;

        document.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
        }, { passive: true });

        document.addEventListener('touchend', (e) => {
            const dx = e.changedTouches[0].clientX - touchStartX;
            const dy = e.changedTouches[0].clientY - touchStartY;
            // Only trigger if horizontal swipe is dominant
            if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dy) > Math.abs(dx)) return;

            // Overlay viewer handles its own touch gestures; don't interfere.
            if (document.querySelector('overlay-viewer')?._visible) return;

            const isCollapsed = document.documentElement.classList.contains('sidebar-collapsed');
            if (dx > 0 && isCollapsed && touchStartX < 40) {
                // Swipe right from left edge: navigate up if in a non-root
                // directory within the file browser, otherwise open sidebar.
                const browser = document.getElementById('main-file-browser');
                const isOverlayOpen = document.querySelector('overlay-viewer')?._visible;
                const isFilesTab = window.appState?.activeTabId === 'files';
                const rootDir = window.appState?.rootDir;
                const currentPath = browser?.currentPath;
                const isRoot = !currentPath || currentPath === '/' ||
                               (rootDir && currentPath === rootDir);

                if (isFilesTab && !isOverlayOpen && !isRoot) {
                    browser.navigateUp();
                } else {
                    document.documentElement.classList.remove('sidebar-collapsed');
                }
            } else if (dx < 0 && !isCollapsed) {
                // Swipe left -> close sidebar
                document.documentElement.classList.add('sidebar-collapsed');
            }
        }, { passive: true });
    }

    // Callout menu: tap-toggle fallback for iOS Safari (where :focus-within is unreliable)
    document.querySelectorAll('.menu-container').forEach(container => {
        const trigger = container.querySelector('.menu-trigger');
        if (!trigger) return;
        trigger.addEventListener('click', (e) => {
            // Close any other open callout menus first
            document.querySelectorAll('.menu-container.callout-open').forEach(other => {
                if (other !== container) other.classList.remove('callout-open');
            });
            container.classList.toggle('callout-open');
        });
    });
    // Close callout menus when clicking outside
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.menu-container')) {
            document.querySelectorAll('.menu-container.callout-open').forEach(c => c.classList.remove('callout-open'));
        }
    });
    // Also close callout after picking an action
    document.querySelectorAll('.callout-menu button').forEach(btn => {
        btn.addEventListener('click', () => {
            btn.closest('.menu-container')?.classList.remove('callout-open');
        });
    });
    
    // Machine info display
    updateMachineInfo();

    const logoutBtn = Array.from(document.querySelectorAll('.nav-item')).find(item => item.title === 'Logout');
    logoutBtn?.addEventListener('click', async () => {
        const activeMachine = getActiveMachine();
        const machineName = activeMachine?.name || 'Self';

        // Dirty check before logout
        const { hasDirtyEditors, hasTerminals } = hasDirtyToolState();
        if (hasDirtyEditors || hasTerminals) {
            const warnings = [];
            if (hasDirtyEditors) warnings.push('unsaved editor changes');
            if (hasTerminals) warnings.push('active terminal connections');
            const confirmed = await showConfirmDialog(
                'Logout?',
                `You have ${warnings.join(' and ')}. Logging out will close all tool tabs. Continue?`
            );
            if (!confirmed) return;
        }

        // Clear only this machine's token
        await logout(activeMachine);

        if (activeMachine?.isSelf) {
            // Self logout — must re-auth, no escape
            await ensureAuthenticatedMachine(activeMachine, { required: true });
        } else {
            // Remote logout — fall back to Self
            closeAllToolTabs();
            setActiveMachine('Self');
            window.appState.config = loadConfig();

            // Reset browser to root
            const browser = document.getElementById('main-file-browser');
            if (browser) {
                const targetPath = window.appState.rootDir || '/';
                if (browser.currentPath === targetPath) {
                    browser.reload();
                } else {
                    browser.currentPath = targetPath;
                }
            }

            // Refresh sidebar and header
            await refreshSidebarState();
            updateMachineInfo();

            // Verify Self auth — if expired, pop auth box
            const selfMachine = getActiveMachine();
            const status = await checkAuthStatus(selfMachine);
            if (!status.valid) {
                await ensureAuthenticatedMachine(selfMachine, { required: true });
            }

            showToast(`Logged out from ${machineName}`, 'info');
        }
    });

    window.addEventListener('machine-changed', async (event) => {
        updateMachineInfo();
        showToast('Switched machine', 'success');
    });
}

function updateBreadcrumbs(path) {
    const bar = document.querySelector('.breadcrumb-list');
    if (!bar) return;
    
    bar.innerHTML = '';
    
    const rootDir = window.appState.rootDir;
    let relativePath = path;
    let isJailed = false;
    
    if (rootDir && path.startsWith(rootDir)) {
        isJailed = true;
        relativePath = path.substring(rootDir.length);
        if (!relativePath.startsWith('/')) {
            relativePath = '/' + relativePath;
        }
    }
    
    const parts = relativePath.split('/').filter(p => p);
    
    // Root
    const rootSpan = document.createElement('span');
    rootSpan.className = 'breadcrumb-item';
    rootSpan.textContent = '/';
    rootSpan.onclick = () => {
        document.getElementById('main-file-browser').currentPath = rootDir || '/';
    };
    bar.appendChild(rootSpan);
    
    let builtPath = isJailed ? rootDir : '';
    parts.forEach((part, index) => {
        if (builtPath.endsWith('/')) {
            builtPath = builtPath.substring(0, builtPath.length - 1);
        }
        builtPath += '/' + part; 
        const currentHook = builtPath;
        
        const sep = document.createElement('span');
        sep.className = 'breadcrumb-separator icon icon-sm';
        sep.textContent = 'keyboard_arrow_right';
        bar.appendChild(sep);
        
        const item = document.createElement('span');
        if (index === parts.length - 1) {
            item.className = 'breadcrumb-current';
        } else {
            item.className = 'breadcrumb-item';
            item.onclick = () => {
                document.getElementById('main-file-browser').currentPath = currentHook;
            };
        }
        item.textContent = part;
        bar.appendChild(item);
    });
}



/**
 * Show settings modal
 */
function showSettings() {
    const overlay = document.getElementById('setting-overlay');
    if (overlay) {
        overlay.style.display = 'flex';
    }
}

/**
 * Update machine info display in header
 */
function updateMachineInfo() {
    const hostInfoEl = document.querySelector('.host-info');
    if (!hostInfoEl) return;
    
    const machine = getActiveMachine();
    if (machine) {
        hostInfoEl.textContent = `@${machine.name || machine.url || 'Unknown'}`;
    } else {
        hostInfoEl.textContent = 'Not connected';
    }
}

function initLoginOverlay() {
    if (loginOverlayInitialized) return;
    loginOverlayInitialized = true;

    const form = document.getElementById('login-form');
    form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        await handleLoginSubmit();
    });

    // Escape key to dismiss login overlay when not required
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !loginOverlayRequired) {
            const overlay = document.getElementById('login-overlay');
            if (overlay && overlay.style.display !== 'none') {
                e.preventDefault();
                hideLoginOverlay();
                if (pendingLoginResolver) {
                    pendingLoginResolver(false);
                    pendingLoginResolver = null;
                }
            }
        }
    });

    // Click outside login-overlay-container to dismiss when not required
    const loginOverlay = document.getElementById('login-overlay');
    if (loginOverlay) {
        loginOverlay.addEventListener('click', (e) => {
            if (!loginOverlayRequired && e.target === loginOverlay) {
                hideLoginOverlay();
                if (pendingLoginResolver) {
                    pendingLoginResolver(false);
                    pendingLoginResolver = null;
                }
            }
        });
    }
}

function showLoginOverlay(machine, { required = true } = {}) {
    loginOverlayRequired = required;
    const overlay = document.getElementById('login-overlay');
    const subtitle = document.getElementById('login-subtitle');
    const titleMachine = machine?.name || 'this host';

    if (subtitle) {
        subtitle.textContent = `Authenticate to access ${titleMachine}.`;
    }

    pendingLoginMachine = machine;

    clearLoginError();
    if (overlay) overlay.style.display = 'flex';

    const input = document.getElementById('login-secret');
    if (input) {
        input.value = '';
        requestAnimationFrame(() => input.focus());
    }
}

function hideLoginOverlay() {
    const overlay = document.getElementById('login-overlay');
    if (overlay) overlay.style.display = 'none';
}

function showLoginError(message) {
    const el = document.getElementById('login-error');
    if (!el) return;
    el.textContent = message;
    el.classList.add('show');
}

function clearLoginError() {
    const el = document.getElementById('login-error');
    if (!el) return;
    el.textContent = '';
    el.classList.remove('show');
}

async function handleLoginSubmit() {
    const secretInput = document.getElementById('login-secret');
    const submitBtn = document.getElementById('login-submit-btn');

    const secret = secretInput?.value?.trim();
    const machine = pendingLoginMachine;

    if (!secret || !machine) {
        showLoginError('Secret is required.');
        return false;
    }

    clearLoginError();
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Signing in...';
    }

    try {
        await login(secret, machine);
        hideLoginOverlay();
        pendingLoginMachine = null;
        if (pendingLoginResolver) {
            pendingLoginResolver(true);
            pendingLoginResolver = null;
        }
        return true;
    } catch (error) {
        showLoginError(error.message || 'Authentication failed.');
        return false;
    } finally {
        if (secretInput) secretInput.value = '';
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Login';
        }
    }
}

export async function ensureAuthenticatedMachine(machine, { required = true } = {}) {
    if (!machine) return false;

    const status = await checkAuthStatus(machine);
    if (status.valid) {
        hideLoginOverlay();
        window.appState.rootDir = status.root_dir || null;
        if (window.appState.rootDir && (window.appState.currentPath === '/' || window.appState.currentPath === '')) {
            window.appState.currentPath = window.appState.rootDir;
        }
        return true;
    }

    showLoginOverlay(machine, { required });
    const loggedIn = await new Promise((resolve) => {
        pendingLoginResolver = resolve;
    });

    if (!loggedIn) {
        return false;
    }

    const verified = await checkAuthStatus(machine);
    if (verified.valid) {
        window.appState.rootDir = verified.root_dir || null;
        if (window.appState.rootDir && (window.appState.currentPath === '/' || window.appState.currentPath === '')) {
            window.appState.currentPath = window.appState.rootDir;
        }
        return true;
    }
    return false;
}

// Start the app when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

/**
 * Fix iOS safe-area-inset-top inconsistency.
 *
 * iOS WebKit evaluates env(safe-area-inset-top) per-element at
 * computed-value time. In standalone (PWA) mode, position:fixed
 * elements get 0 while normal-flow elements get the correct inset;
 * in Safari the behaviour reverses. Defining --safe-top as
 * env(...) in :root doesn't help because CSS custom properties
 * re-evaluate env() on each element that inherits them.
 *
 * Fix: probe the actual pixel value via getComputedStyle (which
 * resolves env() to a concrete px), then set --safe-top as an
 * explicit px string -- bypassing env() entirely for all consumers.
 */
(function fixSafeAreaTop() {
    function probe() {
        const root = document.documentElement;

        // Normal-flow probe -- matches .header-bar context
        const s = document.createElement('div');
        s.style.cssText = 'padding-top:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none;';
        root.appendChild(s);
        const staticVal = parseFloat(getComputedStyle(s).paddingTop) || 0;
        s.remove();

        // Fixed-position probe -- matches overlay context
        const f = document.createElement('div');
        f.style.cssText = 'padding-top:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none;position:fixed;top:0;left:0;';
        root.appendChild(f);
        const fixedVal = parseFloat(getComputedStyle(f).paddingTop) || 0;
        f.remove();

        const maxVal = Math.max(staticVal, fixedVal, 0);
        if (maxVal > 0) {
            root.style.setProperty('--safe-top', maxVal + 'px');
            console.log('[fixSafeAreaTop] static=' + staticVal + ' fixed=' + fixedVal + ' -> --safe-top=' + maxVal + 'px');
        }
    }

    if (document.readyState === 'complete') probe();
    else window.addEventListener('load', probe);
    window.addEventListener('orientationchange', () => setTimeout(probe, 300));
})();
